<div class="flex flex-col items-center justify-center gap-6">
  <div class="flex items-center justify-center public-container">
    @if (registrationNotification &&
      registrationNotification !== registrationNotificationValues.resend_confirmation_mail_success &&
      registrationNotification !== registrationNotificationValues.limit_exceeded_exception) {
      <pin-notification type="alert" customClass="mb-6" class="w-full">
        @switch (registrationNotification) {
          @case (registrationNotificationValues.customer_already_registered) {
            This account has already been registered.
          }
          @case (registrationNotificationValues.username_already_exists) {
            Username is already taken. Please try again with a different username.
          }
          @case (registrationNotificationValues.email_already_exists) {
            This email address has already been used to register one or more existing DCP users.<br>
            If you need to retrieve your account information, return to the login screen and click "Forgot username or password?".<br>
            If you manage more than one customer account, please contact the institution that placed your funds for assistance in linking all the accounts.
          }
          @case (registrationNotificationValues.data_validation_error) {
            The Account ID, Tax ID/Other ID, or email address is not correct.
          }
          @case (registrationNotificationValues.self_registration_disabled) {
            Self-registration has been disabled.
          }
          @case (registrationNotificationValues.try_again_later) {
            This page is currently unavailable. Please try again later.
          }
        }
      </pin-notification>
    }
  </div>
  @if (isRegistrationPage) {
    <div class="flex flex-col justify-center public-container">

      <div class="flex flex-row items-center justify-center registration-form">
        <form class="flex flex-col gap-4" id="registrationForm" [formGroup]="registrationForm" (ngSubmit)="sendRegistration()">

          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-2">
              <h1 class="dcp-h1" id="user.verification.title">Verify Your Account</h1>
              <p class="dcp-body" id="user.verification.subtitle">To help protect you, we need you to verify your account.</p>
            </div>

            <div id="user.verification">
              <div class="flex flex-row gap-4 items-center justify-between">
                <mat-form-field class="w-full" appearance="outline" id="user.verification.extAcctId">
                  <mat-label>Any one of your ICS or CDARS Account IDs</mat-label>

                  <input matInput
                         placeholder="Any one of your ICS or CDARS Account IDs"
                         type="text"
                         formControlName="accountIdentifier"
                         i18n-placeholder="@@ext-account-id-placeholder"
                         maxlength="80"
                         autocomplete="off">
                  @if (registrationForm.controls['accountIdentifier'].hasError('required')) {
                    <mat-error id="ext.account.id.required" i18n="ext.account.id.required">
                      Account ID is required
                    </mat-error>
                  }
                </mat-form-field>
                <pin-tooltip [isNewTooltip]="true"
                             tooltipId="transactionAccountTooltip"
                             ariaLabelValue="Additional Information Regarding Transaction Account Number."
                             iconClass="flex items-center"
                             tooltipContainerClass="registration-new-tooltip"
                             tooltipInfo="Please use any of your ICS or CDARS account IDs. For assistance locating an ID, please contact your institution.">
                </pin-tooltip>
              </div>

              <div class="flex flex-row gap-4 items-center justify-between">
                <mat-form-field class="w-full" appearance="outline" id="user.verification.unifiedTaxId">
                  <mat-label>Tax ID/Other ID (Last 4 characters)</mat-label>

                  <input matInput placeholder="Tax ID/Other ID (Last 4 characters)"
                         type="text" formControlName="unifiedTaxIdLast4Digits"
                         i18n-placeholder="@@unified-tax-id-placeholder"
                         minlength="4" maxlength="4"
                         (keyup)="registrationForm.controls['unifiedTaxIdLast4Digits'].markAsTouched()"
                         autocomplete="new-password">

                  @if (registrationForm.controls['unifiedTaxIdLast4Digits'].hasError('required')) {
                    <mat-error id="unified.tax.id.required" i18n="@@registration.unified.tax.id.required">
                      Tax ID/Other ID is required
                    </mat-error>
                  }

                  @if (registrationForm.controls['unifiedTaxIdLast4Digits'].hasError('minlength')) {
                    <mat-error id="unified.tax.id.minlength"
                               i18n="@@registration.unified.tax.id.minLength">
                      Must be 4 characters long
                    </mat-error>
                  }
                </mat-form-field>

                <pin-tooltip [isNewTooltip]="true"
                             tooltipId="unifiedTaxIdTooltip"
                             ariaLabelValue="Additional Information Regarding Tax ID/Other ID."
                             iconClass="flex items-center"
                             tooltipContainerClass="registration-new-tooltip"
                             tooltipInfo="Enter the last 4 characters of the Tax ID/Other ID associated with the above account.">
                </pin-tooltip>
              </div>

              <div class="flex flex-row gap-4 items-center justify-between">
                <mat-form-field class="w-full" appearance="outline" id="user.verification.email">
                  <mat-label>Email you provided for the account</mat-label>

                  <input matInput formControlName="email"
                         placeholder="Email you provided for the account"
                         type="text" maxlength="50"
                         i18n-placeholder="@@email-placeholder"
                         autocomplete="new-password">
                  @if (registrationForm.controls['email'].hasError('required')) {
                    <mat-error id="email.required" i18n="@@registration.email.required">
                      Email is required
                    </mat-error>
                  }

                  @if (registrationForm.controls['email'].hasError('email')) {
                    <mat-error id="email.invalid" i18n="@@registration.email.invalid">
                      Email is invalid
                    </mat-error>
                  }
                </mat-form-field>
                <pin-tooltip [isNewTooltip]="true"
                             tooltipId="emailTooltip"
                             ariaLabelValue="Additional Information Regarding Email."
                             iconClass="flex items-center"
                             tooltipContainerClass="registration-new-tooltip"
                             tooltipInfo="Please enter the email you provided to your institution. If you need assistance, please contact your institution.">
                </pin-tooltip>
              </div>
            </div>
          </div>
          <!-- -->
          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-2">
              <h1 class="dcp-h1" id="user.credentials.title">Create Your Username and Password</h1>
              <p class="dcp-body" id="user.credentials.subtitle">To access the accounts associated to you, you must first create a Depositor Control Panel account</p>
            </div>
            <div id="user.credentials">

              <mat-form-field class="w-full" appearance="outline" id="user.credentials.firstName">
                <mat-label>First Name</mat-label>

                <input matInput placeholder="First name" formControlName="firstName" type="text" pinTrimField
                       maxlength="50" i18n-placeholder="@@firstName-placeholder">

                @if (registrationForm.controls['firstName'].hasError('required')) {
                  <mat-error id="firstName.required" i18n="@@registration.firstName.required">
                    First Name is required
                  </mat-error>
                }
                @if (registrationForm.controls['firstName'].hasError('nonAsciiPrintable')) {
                  <mat-error>This field cannot contain invalid characters.</mat-error>
                }
              </mat-form-field>

              <mat-form-field class="w-full" appearance="outline" id="user.credentials.lastName">
                <mat-label>Last Name</mat-label>

                <input matInput placeholder="Last Name" formControlName="lastName" type="text" pinTrimField
                       maxlength="50" i18n-placeholder="@@lastName-placeholder">
                @if (registrationForm.controls['lastName'].hasError('required')) {
                  <mat-error id="lastName.required" i18n="@@registration.lastName.required">
                    Last Name is required
                  </mat-error>
                }
                @if (registrationForm.controls['lastName'].hasError('nonAsciiPrintable')) {
                  <mat-error>This field cannot contain invalid characters.</mat-error>
                }
              </mat-form-field>

              <mat-form-field class="w-full" appearance="outline" id="user.credentials.username">
                <mat-label>Username</mat-label>

                <input matInput placeholder="Username" formControlName="username" type="text"
                       maxlength="50" i18n-placeholder="@@username-placeholder">

                @if (registrationForm.controls['username'].hasError('required')) {
                  <mat-error id="username.required" i18n="@@registration.username.required">Username is required</mat-error>
                }

                @if (registrationForm.controls['username'].hasError('nonAsciiPrintable')) {
                  <mat-error>This field cannot contain invalid characters.</mat-error>
                }
              </mat-form-field>

              <div>
                <mat-form-field class="w-full" appearance="outline" id="user.credentials.password">
                  <mat-label>Password</mat-label>

                  <pin-password-input formControlName="password" placeholder="Password"
                                      [allowPaste]="false"
                                      (passwordFocus)="passwordTooltip.onPasswordFocus($event)"
                                      (passwordBlur)="onPasswordBlur()"></pin-password-input>
                  @if (registrationForm.controls['password'].hasError('required')) {
                    <mat-error id="password.required" i18n="@@password.required">Password is required</mat-error>
                  }

                  @if (registrationForm.controls['password'].hasError('pattern')) {
                    <mat-error>Password does not meet the requirements</mat-error>
                  }
                </mat-form-field>
                <pin-password-info [positionLeft]='passwordTooltip.passwordTooltipLeft'
                                   [positionTop]='passwordTooltip.passwordTooltipTop'
                                   [show]='passwordTooltip.showPasswordTooltip'
                                   (mouseenter)="passwordTooltip.isPasswordTooltipFocused=true"
                                   (mouseleave)="passwordTooltip.isPasswordTooltipFocused=false"
                                   customClass="registration-password-helper">
                </pin-password-info>
              </div>

              <mat-form-field class="w-full" appearance="outline" id="user.credentials.verify.password">
                <mat-label>Verify Password</mat-label>

                <pin-password-input formControlName="passwordConfirmation" placeholder="Verify Password" [allowPaste]="false"></pin-password-input>
                @if (registrationForm.controls['passwordConfirmation'].hasError('required')) {
                  <mat-error id="verify.password.invalid" i18n="@@verify.password.required">
                    You must verify your password
                  </mat-error>
                }

                @if (registrationForm.controls['passwordConfirmation'].hasError('pwdDoesNotMatch')) {
                  <mat-error id="verify.password.match" i18n="@@verify.password.match">
                    Verify Password does not match the Password entered
                  </mat-error>
                }
              </mat-form-field>
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <pin-button buttonStyle="filled" buttonId="verify-and-create-my-account-button" [isDisabled]="loading">VERIFY AND CREATE MY ACCOUNT</pin-button>

            <div class="flex flex-row justify-center items-center">
              <div class="pb-1" data-testid="registration-already-have-account">Already verified your account?</div>
              <pin-button buttonStyle="link" customClass="pb-1!" buttonId="registration-login-link" (buttonClick)="navigateToLogin($event)" redirectUrl="">Log In</pin-button>
            </div>
          </div>
        </form>
      </div>
    </div>
  } @else {
    <div class="flex flex-col justify-center public-container large">
      @if (registrationNotification === registrationNotificationValues.limit_exceeded_exception) {
        <pin-notification type="alert" customClass="mb-6 post-registration-message">
          Too many attempts. Please try again later.
        </pin-notification>
      }
      @if (registrationNotification === registrationNotificationValues.resend_confirmation_mail_success) {
        <pin-notification type="success" customClass="mb-6 post-registration-message">
          You have successfully resent the confirmation email. <br/>
          Please check your email.
        </pin-notification>
      }

      <div class="flex flex-col gap-6">
        <div class="flex flex-row gap-4">
          <mat-icon class="md-37 primary-color">email</mat-icon>
          <div id="title" class="dcp-h2">
            Please Check Your Email
          </div>
        </div>
        <div id="check-email-content" class="flex flex-col gap-6">
          <p id="first-paragraph" class="dcp-body">
            An email was sent to the email address used at registration.
          </p>
          <p id="second-paragraph" class="dcp-body">
            If you do not receive the email within a few minutes, please check your spam folder or ensure no-reply&#64;depositorcontrol.com is not being blocked by your email
            service provider.
          </p>
          <p id="first-question" class=" dcp-body-bold">
            Still didn’t receive the email?
          </p>
          <p id="last-paragraph" class="dcp-body">
            <pin-button customClass="p-0!" buttonStyle="link" buttonId="send-email-again-button" (buttonClick)="resendConfirmationEmail()" redirectUrl="" [isDisabled]="resendingEmailLocked">Send the email again</pin-button>
              {{ allowResendIn }}, or
            <pin-button customClass="p-0! min-w-fit!" buttonStyle="link" buttonId="log-in-button" redirectUrl="/login">log in</pin-button>
            with your username
            and password and click 'resend confirmation email'.
          </p>
        </div>
      </div>
    </div>
  }
</div>
@if (loading) {
  <div class="pin-spinner fullscreen">
    <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
  </div>
}
